// @ts-nocheck
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';

const SubscriptionsTab: React.FC = () => {
  const { data: subscriptionData, isLoading, error: queryError } = useQuery({
    queryKey: ['subscription-overview'],
    queryFn: async () => {
      console.log('🔍 Starting subscription query...');
      
      // Get active subscriptions from Stripe-synced subscriptions table
      const { data: activeMemberships, error: activeError } = await supabase
        .rpc('get_active_stripe_subscriptions');

      console.log('📊 RPC Response:', { 
        activeMemberships, 
        activeError,
        count: activeMemberships?.length 
      });

      if (activeError) {
        console.error('❌ RPC Error:', activeError);
        throw activeError;
      }

      // Calculate MRR from active memberships
      const mrr = (activeMemberships || []).reduce((sum, sub) => {
        return sum + (sub.monthly_price || 0);
      }, 0);

      // Churn rate - placeholder for now
      const churnRate = 0;

      console.log('✅ Returning data:', {
        activeMembershipsCount: activeMemberships?.length,
        mrr,
        churnRate
      });

      return {
        activeMemberships: activeMemberships || [],
        mrr,
        churnRate,
      };
    },
  });

  console.log('🎯 Query State:', { 
    isLoading, 
    hasData: !!subscriptionData,
    hasError: !!queryError,
    activeMembershipsCount: subscriptionData?.activeMemberships?.length 
  });

  const exportToCSV = () => {
    if (!subscriptionData) return;

    const headers = ['User', 'Email', 'Membership', 'Price', 'Start Date', 'End Date', 'Status'];
    const rows = subscriptionData.activeMemberships.map(sub => [
      sub.user_name || 'Unknown',
      sub.email || 'No email',
      sub.membership_name || 'Unknown',
      `$${(sub.monthly_price || 0).toFixed(2)}`,
      sub.billing_cycle_start,
      sub.billing_cycle_end,
      sub.status ? 'Active' : 'Inactive',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Exported to CSV');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading subscription data...</div>;
  }

  if (queryError) {
    console.error('❌ Query Error:', queryError);
    return <div className="text-center py-8 text-red-600">Error loading subscriptions: {queryError.message}</div>;
  }

  const { activeMemberships, mrr, churnRate } = subscriptionData || {
    activeMemberships: [],
    mrr: 0,
    churnRate: 0,
  };

  console.log('🖥️ Rendering with data:', { 
    activeMembershipsCount: activeMemberships?.length,
    firstSub: activeMemberships?.[0],
    kellerRows: activeMemberships?.filter((s: any) => s.email === 'daughertykeller@gmail.com')
  });

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">MRR (Monthly Recurring Revenue)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(mrr)}</div>
            <div className="flex items-center text-sm text-green-600 mt-1">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>+12% from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeMemberships.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{churnRate.toFixed(1)}%</div>
            <div className="flex items-center text-sm text-red-600 mt-1">
              <TrendingDown className="h-4 w-4 mr-1" />
              <span>Target: &lt; 5%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Subscriptions Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Active Subscriptions</CardTitle>
              <CardDescription>All currently active membership subscriptions</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => toast.info('Syncing with Stripe...')}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Stripe
              </Button>
              <Button variant="outline" onClick={exportToCSV}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Membership</TableHead>
                <TableHead className="text-right">Monthly Price</TableHead>
                <TableHead>Expiration Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeMemberships.map((sub: any) => (
                <TableRow key={sub.id}>
                  <TableCell className="font-medium">{sub.user_name || 'Unknown'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{sub.email || 'No email'}</TableCell>
                  <TableCell>{sub.membership_name || 'Unknown'}</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(sub.monthly_price || 0)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {sub.expires_at 
                      ? `Expires: ${formatDate(sub.expires_at)}`
                      : 'No expiration'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="default">Active</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionsTab;
